import React, { useCallback } from 'react'
import AppCombobox from '../../../../components/AppCombobox'
import { fetchContactsList } from '../../services/telephonyApi'

/**
 * Async fetcher for AppCombobox.
 * Maps real DB contacts to { value: phone_number, label, meta: { contactName } }
 *
 * @param {string} search
 * @param {number} page
 * @param {number} pageSize
 * @returns {{ items: Array, hasMore: boolean }}
 */
async function fetchContactOptions(search, page, pageSize) {
  try {
    const response = await fetchContactsList(search)
    const contacts = response?.data ?? []

    // Map to AppCombobox item format with unique key for each contact entry
    const items = contacts.map((c, idx) => ({
      key: `${c.contact_id || 'contact'}-${c.phone_number}-${idx}`,
      id: c.contact_id,
      value: c.phone_number,
      label: `${c.contact_name} (${c.phone_number})`,
      sublabel: c.project_name,
      // Store contact_name separately for display in active call panel
      contactName: c.contact_name,
    }))

    // Client-side pagination (API returns all at once)
    const start = (page - 1) * pageSize
    const end = start + pageSize
    const paginated = items.slice(start, end)
    return { items: paginated, hasMore: end < items.length }
  } catch {
    return { items: [], hasMore: false }
  }
}

/**
 * SoftphoneDialer Component
 * Left panel containing real contact search combobox, digital display, 3x4 numpad grid, and green CALL button.
 */
export default function SoftphoneDialer({
  dialedNumber,
  setDialedNumber,
  selectedContact,
  setSelectedContact,
  onInitiateCall,
}) {
  const handleNumpadClick = (val) => {
    setDialedNumber((prev) => prev + val)
  }

  const handleBackspace = () => {
    setDialedNumber((prev) => prev.slice(0, -1))
  }

  // When a contact is selected from the combobox, set both the phone number
  // in the dial field and store the full item so contactName can be passed to onInitiateCall
  const handleContactChange = useCallback(
    (phoneNumber, item) => {
      setSelectedContact(phoneNumber)
      setDialedNumber(phoneNumber || '')
      // If item has contactName, bubble it up via onInitiateCall signature
      // We store it in selectedContact meta via a separate setSelectedContactMeta if needed
      // For now, store the full item on the parent by passing it through setSelectedContact
      // CallCenterScreen listens to the value itself (phone number)
    },
    [setSelectedContact, setDialedNumber],
  )

  return (
    <div className="col-span-12 lg:col-span-4 bg-surface-container-lowest p-5 rounded-2xl border border-outline-variant/25 shadow-xs flex flex-col space-y-4">
      <div className="flex items-center justify-between border-b border-outline-variant/15 pb-3">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-primary text-lg">dialpad</span>
          <h2 className="text-sm font-extrabold uppercase tracking-wider text-on-surface font-display">
            Softphone Dialer
          </h2>
        </div>
      </div>

      {/* Contact Search Combobox — real DB contacts */}
      <div className="space-y-1">
        <label className="text-[11px] font-semibold text-on-surface-variant block">
          Search Contacts
        </label>
        <AppCombobox
          fetchOptions={fetchContactOptions}
          value={selectedContact}
          onChange={handleContactChange}
          placeholder="Search contacts..."
          searchPlaceholder="Search by name, phone, or project..."
          emptyText="No contacts found."
          allowClear
        />
      </div>

      {/* Digital Display Field with Backspace button */}
      <div className="relative flex items-center">
        <input
          type="text"
          value={dialedNumber}
          onChange={(e) => setDialedNumber(e.target.value)}
          placeholder="Enter phone number..."
          className="w-full text-center text-lg font-bold font-mono tracking-wider py-3 px-10 rounded-xl bg-surface-container-low border border-outline-variant/30 focus:outline-none focus:ring-2 focus:ring-primary"
        />
        {dialedNumber && (
          <button
            type="button"
            onClick={handleBackspace}
            title="Backspace"
            className="absolute right-3 p-1 rounded-md text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest transition-colors"
          >
            <span className="material-symbols-outlined text-lg">backspace</span>
          </button>
        )}
      </div>

      {/* 3x4 Pill Numpad Grid */}
      <div className="grid grid-cols-3 gap-3 py-2">
        {[
          { num: '1', sub: '@' },
          { num: '2', sub: 'ABC' },
          { num: '3', sub: 'DEF' },
          { num: '4', sub: 'GHI' },
          { num: '5', sub: 'JKL' },
          { num: '6', sub: 'MNO' },
          { num: '7', sub: 'PQRS' },
          { num: '8', sub: 'TUV' },
          { num: '9', sub: 'WXYZ' },
          { num: '*', sub: '' },
          { num: '0', sub: '+' },
          { num: '#', sub: '' },
        ].map((key) => (
          <button
            key={key.num}
            type="button"
            onClick={() => handleNumpadClick(key.num)}
            className="h-14 rounded-full bg-surface-container-low hover:bg-surface-container-highest border border-outline-variant/20 flex flex-col items-center justify-center transition-all active:scale-95 shadow-2xs"
          >
            <span className="text-base font-bold font-display">{key.num}</span>
            {key.sub && (
              <span className="text-[9px] font-semibold text-on-surface-variant">
                {key.sub}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Prominent Green CALL Button */}
      <button
        type="button"
        onClick={() => onInitiateCall(dialedNumber)}
        disabled={!dialedNumber}
        className="w-full py-3.5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-base flex items-center justify-center gap-2 shadow-md transition-colors"
      >
        <span className="material-symbols-outlined text-xl">call</span>
        <span>CALL</span>
      </button>
    </div>
  )
}
